import type { Event } from "openapi/api/model"

/**
 * The calendar's arithmetic — canvas 33b and 33c — kept out of the components.
 *
 * The month grid, the week's time grid and the lane packing that stops two
 * overlapping meetings from becoming three slivers are all pure functions of
 * the event list and a cursor date, so every one of them can be checked without
 * a browser. The views render what these return.
 */

export const CALENDAR_VIEWS = ["month", "week", "day"] as const
export type CalendarView = (typeof CALENDAR_VIEWS)[number]

/** 33b's legend, which is also the set `event_type` actually takes. */
export const EVENT_TYPES = ["interview", "screening", "meeting", "other"] as const
export type EventType = (typeof EVENT_TYPES)[number]

/**
 * An event type's colour, from the app's one semantic ramp.
 *
 * The pairing looks odd written down — the *event* type "screening" takes the
 * *stage* token `hired`, and the event type "meeting" takes the stage token
 * `screening` — but the ramp is a set of hues rather than a set of meanings,
 * and these are the four 33b draws. Using tokens rather than the artboard's
 * literals is what gives the calendar a light theme, which a dark-only artboard
 * could not describe.
 */
export const EVENT_STAGE: Record<EventType, string> = {
  interview: "shortlist",
  screening: "hired",
  meeting: "screening",
  other: "longlist",
}

export const eventType = (event: Event): EventType => {
  const value = (event.event_type ?? "").toLowerCase()
  return (EVENT_TYPES as readonly string[]).includes(value) ? (value as EventType) : "other"
}

/** The grid's first and last hour, per 33c. */
export const DAY_START_HOUR = 5
export const DAY_END_HOUR = 24
/** One hour's height, and where the grid opens. */
export const HOUR_HEIGHT = 48
export const OPENS_AT_HOUR = 8

export function startOfDay(at: Date): Date {
  const copy = new Date(at)
  copy.setHours(0, 0, 0, 0)
  return copy
}

export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

/**
 * The Sunday that opens `at`'s week — 33b and 33c both start on Sunday.
 *
 * By calendar date, not by subtracting 24-hour days. A day is not always 24
 * hours: across a daylight-saving change, three "days" back from midnight on
 * Wednesday 11 March 2026 in Toronto lands on Saturday the 7th at 23:00 — so
 * that week's grid ran Saturday to Friday and dropped its last Saturday.
 */
export function startOfWeek(at: Date): Date {
  const start = startOfDay(at)
  start.setDate(start.getDate() - start.getDay())
  return start
}

export function addDays(at: Date, days: number): Date {
  const copy = new Date(at)
  copy.setDate(copy.getDate() + days)
  return copy
}

export function addMonths(at: Date, months: number): Date {
  const copy = new Date(at)
  // To the first before shifting: adding a month to the 31st otherwise lands
  // two months on, because the intermediate month has no 31st.
  copy.setDate(1)
  copy.setMonth(copy.getMonth() + months)
  const days = new Date(copy.getFullYear(), copy.getMonth() + 1, 0).getDate()
  copy.setDate(Math.min(at.getDate(), days))
  return copy
}

export interface GridDay {
  date: Date
  inMonth: boolean
  isToday: boolean
}

/**
 * The month as whole weeks, Sunday-first.
 *
 * Exactly the weeks the month touches — five for a September that runs Tuesday
 * to Wednesday, six for a month that needs one. 33b draws September in five and
 * ends the grid on 3 October, so a fixed six would have added a whole redundant
 * week of the next month under a September that had already finished.
 *
 * The grid fills the space it is given, so a five-row month simply has taller
 * rows; nothing on the page moves when the count changes.
 */
export function monthGrid(cursor: Date, now = new Date()): GridDay[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
  const start = startOfWeek(first)
  const today = startOfDay(now)
  const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()
  const weeks = Math.ceil((first.getDay() + days) / 7)

  return Array.from({ length: weeks * 7 }, (_, index) => {
    const date = addDays(start, index)
    return {
      date,
      inMonth: date.getMonth() === cursor.getMonth(),
      isToday: sameDay(date, today),
    }
  })
}

/** The seven days of `cursor`'s week. */
export function weekGrid(cursor: Date, now = new Date()): GridDay[] {
  const start = startOfWeek(cursor)
  const today = startOfDay(now)
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(start, index)
    return { date, inMonth: true, isToday: sameDay(date, today) }
  })
}

/**
 * The one day of `cursor`, in the shape the week grid uses.
 *
 * The day view is the week's grid with a single column, so it wants the same
 * `GridDay` — including `isToday`, which a hand-built literal kept forgetting
 * and which is the only thing marking the column you are standing in.
 */
export function dayGrid(cursor: Date, now = new Date()): GridDay[] {
  return [{ date: new Date(cursor), inMonth: true, isToday: sameDay(cursor, now) }]
}

/** The hour labels down the side of a time grid. */
export function hourRows(): number[] {
  return Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i)
}

export const startOf = (event: Event): Date | undefined => {
  const at = Date.parse(event.start_at ?? "")
  return Number.isFinite(at) ? new Date(at) : undefined
}

export const endOf = (event: Event): Date | undefined => {
  const at = Date.parse(event.end_at ?? "")
  return Number.isFinite(at) ? new Date(at) : undefined
}

/**
 * The span of time a view is showing: `[start, end)`.
 *
 * A month is the calendar month rather than the grid's leading and trailing
 * days, because "September 2026 · 18 events" is a claim about September.
 */
export function periodRange(cursor: Date, view: CalendarView): { start: Date; end: Date } {
  if (view === "month") {
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    return { start, end: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1) }
  }
  const start = view === "week" ? startOfWeek(cursor) : startOfDay(cursor)
  return { start, end: addDays(start, view === "week" ? 7 : 1) }
}

/**
 * The events happening in the period a view is showing.
 *
 * By overlap, not by start: the time grid draws every event that touches a
 * day, so a Saturday-night call running into Sunday is in Sunday's column and
 * has to be in Sunday's figures too.
 */
export function eventsInPeriod(events: Event[], cursor: Date, view: CalendarView): Event[] {
  const { start, end } = periodRange(cursor, view)
  return events.filter((event) => overlaps(event, start, end))
}

/**
 * When an event runs, with its end resolved the one way the app resolves it.
 *
 * A missing end, or one before the start, reads as a short meeting. Stated
 * once because the grid, the period filter and the receipts all have to agree
 * about when an event is happening — they did not, which is how an overnight
 * event came to be drawn in a column whose own receipts said it held nothing.
 */
export function spanOf(event: Event): { start: Date; end: Date } | undefined {
  const start = startOf(event)
  if (!start) return undefined
  const end = endOf(event)
  return {
    start,
    end: end && end > start ? end : new Date(start.getTime() + FALLBACK_MINUTES * 60_000),
  }
}

/** Whether an event is happening at any point in `[start, end)`. */
export const overlaps = (event: Event, start: Date, end: Date): boolean => {
  const span = spanOf(event)
  return span ? span.start < end && span.end > start : false
}

/** Events that begin on `day`, earliest first. Unparseable rows are dropped. */
export function eventsOn(events: Event[], day: Date): Event[] {
  return events
    .filter((event) => {
      const at = startOf(event)
      return at ? sameDay(at, day) : false
    })
    .sort((a, b) => (startOf(a)?.getTime() ?? 0) - (startOf(b)?.getTime() ?? 0))
}

export interface PlacedEvent {
  event: Event
  /** Minutes from midnight, clamped into the grid. */
  top: number
  height: number
  /** Which of `lanes` this event sits in, and how many the cluster needed. */
  lane: number
  lanes: number
  /** The event began before this day, and this block is its continuation. */
  continuesBefore: boolean
  /** It runs on past this day, so the block is cut at midnight. */
  continuesAfter: boolean
}

/**
 * The shortest a block is ever drawn, in minutes — 20px at 48px an hour, the
 * least that holds a legible title. Stated once and used both to size a block
 * and to pack lanes, so the two cannot disagree about where a block ends.
 */
export const MIN_BLOCK_MINUTES = Math.ceil((20 / HOUR_HEIGHT) * 60)

/** A sensible length for an event whose end is missing or before its start. */
const FALLBACK_MINUTES = 30

const minutesInto = (at: Date) => at.getHours() * 60 + at.getMinutes()

/**
 * Lay events onto one day's column of the time grid, packed into lanes.
 *
 * Each event is cut to the part of it that falls on `day`, using real dates,
 * so one that runs past midnight appears in both columns: 22:00–10:00 draws
 * 22:00 to midnight on its first day and 05:00 to 10:00 on the next. A column
 * used to take only events that *started* on it, so that next morning was
 * missing. Without `day`, each event is placed on its own start day.
 *
 * Lanes are counted per *cluster* of events that actually overlap, not per
 * day: 33c is explicit that "Tuesday's 10:00 and 10:30 share the hour in two
 * lanes rather than three slivers", which only works if the 16:00 meeting
 * further down is left at full width rather than squeezed to a third because
 * something else that morning was busy.
 *
 * Overlap is judged by where a block is *drawn*, not where it really ends. A
 * block is never shorter than `MIN_BLOCK_MINUTES`, so a 15-minute call reaches
 * past its own end on screen; packed by its real end, the call after it
 * shared its lane and was drawn on top of it.
 */
export function placeEvents(events: Event[], day?: Date): PlacedEvent[] {
  const gridTop = DAY_START_HOUR * 60
  const gridBottom = DAY_END_HOUR * 60

  const spans = events
    .map((event) => {
      const span = spanOf(event)
      if (!span) return undefined
      const { start, end } = span

      const dayStart = startOfDay(day ?? start)
      const dayEnd = addDays(dayStart, 1)
      if (end <= dayStart || start >= dayEnd) return undefined

      const from = start <= dayStart ? 0 : minutesInto(start)
      const to = end >= dayEnd ? 24 * 60 : minutesInto(end)
      /*
        Wholly outside the 5am–midnight grid, it has no place on it. Clamping
        drew a 04:00–04:30 call as a sliver at 5am — and a block that short
        shows only its title, so nothing on it said it was not at five. The
        month view and the receipts still count it.
      */
      if (to <= gridTop || from >= gridBottom) return undefined

      const top = Math.max(from, gridTop)
      /*
        Never shorter than the minimum, except against the bottom of the grid,
        where there is no room for it. A block keeps its start and gives up the
        height: moving it up to fit stated a time the event did not have, and a
        block that short carries no clock to correct the impression.
      */
      const drawnTo = Math.min(
        Math.max(Math.min(to, gridBottom), top + MIN_BLOCK_MINUTES),
        gridBottom,
      )
      return {
        event,
        from: top,
        to: drawnTo,
        continuesBefore: start < dayStart,
        continuesAfter: end > dayEnd,
      }
    })
    .filter((span): span is NonNullable<typeof span> => Boolean(span))
    .sort((a, b) => a.from - b.from || a.to - b.to)

  const placed: PlacedEvent[] = []
  let cluster: {
    event: Event
    from: number
    to: number
    continuesBefore: boolean
    continuesAfter: boolean
    lane: number
  }[] = []
  let clusterEnd = -1

  const flush = () => {
    if (cluster.length === 0) return
    const lanes = Math.max(...cluster.map((entry) => entry.lane)) + 1
    for (const entry of cluster) {
      placed.push({
        event: entry.event,
        top: entry.from - gridTop,
        height: entry.to - entry.from,
        lane: entry.lane,
        lanes,
        continuesBefore: entry.continuesBefore,
        continuesAfter: entry.continuesAfter,
      })
    }
    cluster = []
    clusterEnd = -1
  }

  for (const span of spans) {
    // A gap means the previous cluster is closed and its lane count is settled.
    if (span.from >= clusterEnd) flush()

    const laneEnds: number[] = []
    for (const entry of cluster) {
      laneEnds[entry.lane] = Math.max(laneEnds[entry.lane] ?? -1, entry.to)
    }
    let lane = laneEnds.findIndex((end) => end <= span.from)
    if (lane === -1) lane = laneEnds.length === 0 ? 0 : laneEnds.length

    cluster.push({ ...span, lane })
    clusterEnd = Math.max(clusterEnd, span.to)
  }
  flush()

  return placed
}

/**
 * Whether the signed-in person turned this invitation down.
 *
 * 33c dims a declined event. The only RSVP the contract carries is
 * `response_status` on each attendee inside `provider_meeting_metadata`, which
 * providers populate and locally-created events do not — so an event nobody
 * synced simply never dims, which is the honest answer rather than a guess.
 *
 * A cancelled event is drawn struck through in 33c and is *not* built: an event
 * carries no status of its own, and there is nowhere for "cancelled" to come
 * from. It is a backend ask.
 */
export function isDeclined(event: Event, email?: string): boolean {
  if (!email) return false
  const attendees = event.provider_meeting_metadata?.attendees ?? []
  return attendees.some(
    (attendee) =>
      attendee.email?.toLowerCase() === email.toLowerCase() &&
      (attendee.response_status ?? "").toLowerCase() === "declined",
  )
}

export interface CalendarReceipts {
  total: number
  interviews: number
  screenings: number
  today: number
}

/**
 * The line under the period label.
 *
 * 33b writes "18 events · 9 interviews · 5 screenings · 1 cancelled · 2 today".
 * The cancelled figure is not here, for the reason `isDeclined` gives: an event
 * has no status, so there is nothing to count.
 */
export function receiptsOf(events: Event[], now = new Date()): CalendarReceipts {
  const today = startOfDay(now)
  let interviews = 0
  let screenings = 0
  let todayCount = 0

  for (const event of events) {
    const type = eventType(event)
    if (type === "interview") interviews += 1
    if (type === "screening") screenings += 1
    // Overlap, so a call that began last night counts on the day it runs into.
    if (overlaps(event, today, addDays(today, 1))) todayCount += 1
  }

  return { total: events.length, interviews, screenings, today: todayCount }
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]
const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** The week header's column labels, and the day view's fuller form of them. */
export const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
export const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
]

/**
 * What the toolbar says it is showing: "September 2026", "Sep 13 – 19, 2026",
 * "Wednesday, Sep 16, 2026".
 *
 * A day names its weekday. Month and week are self-locating — a grid of dates
 * under Sun–Sat headings tells you where you are — but a single column does
 * not, and "Sep 16" alone leaves you counting, so the day is written out in
 * full.
 */
export function periodLabel(cursor: Date, view: CalendarView): string {
  if (view === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
  if (view === "day") {
    return `${WEEKDAYS[cursor.getDay()]}, ${SHORT[cursor.getMonth()]} ${cursor.getDate()}, ${cursor.getFullYear()}`
  }
  const start = startOfWeek(cursor)
  const end = addDays(start, 6)
  const left = `${SHORT[start.getMonth()]} ${start.getDate()}`
  // A week that straddles two months names the second one as well.
  const right =
    start.getMonth() === end.getMonth()
      ? `${end.getDate()}`
      : `${SHORT[end.getMonth()]} ${end.getDate()}`
  return `${left} – ${right}, ${end.getFullYear()}`
}

/** "10:00" — the calendar's clock, in the mono voice the artboards set it in. */
export function clockOf(at: Date): string {
  return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`
}

/** "8 AM", for the rail down the side of a time grid. */
export function hourLabel(hour: number): string {
  if (hour === 0 || hour === 24) return "12 AM"
  if (hour === 12) return "12 PM"
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`
}

/** How the cursor moves when you press the chevrons, per view. */
export function step(cursor: Date, view: CalendarView, direction: 1 | -1): Date {
  if (view === "month") return addMonths(cursor, direction)
  return addDays(cursor, direction * (view === "week" ? 7 : 1))
}
